import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface Document {
  id: string;
  fileName: string;
  documentType: string;
  fileSize: number;
  uploadDate: string;
  status: 'uploaded' | 'processing' | 'error';
  errorMessage?: string;
  projectId?: number;
  personId?: number;
  personName?: string;
  analysisStatus?: string;
}

export interface DocumentsState {
  documents: Document[];
  uploadProgress: Record<string, number>; // fileId -> progress percentage
  isUploading: boolean;
  error: string | null;
}

const initialState: DocumentsState = {
  documents: [],
  uploadProgress: {},
  isUploading: false,
  error: null,
};

const documentsSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    setDocuments: (state, action: PayloadAction<Document[]>) => {
      state.documents = action.payload;
      state.error = null;
    },

    addDocument: (state, action: PayloadAction<Document>) => {
      state.documents.push(action.payload);
    },

    removeDocument: (state, action: PayloadAction<string>) => {
      state.documents = state.documents.filter((doc) => doc.id !== action.payload);
      delete state.uploadProgress[action.payload];
    },

    updateDocumentType: (
      state,
      action: PayloadAction<{ id: string; documentType: string }>
    ) => {
      const doc = state.documents.find((d) => d.id === action.payload.id);
      if (doc) {
        doc.documentType = action.payload.documentType;
      }
    },

    updateDocumentStatus: (
      state,
      action: PayloadAction<{ id: string; status: 'uploaded' | 'processing' | 'error'; errorMessage?: string }>
    ) => {
      const doc = state.documents.find((d) => d.id === action.payload.id);
      if (doc) {
        doc.status = action.payload.status;
        if (action.payload.errorMessage) {
          doc.errorMessage = action.payload.errorMessage;
        }
      }
    },

    setUploadProgress: (state, action: PayloadAction<{ fileId: string; progress: number }>) => {
      state.uploadProgress[action.payload.fileId] = action.payload.progress;
    },

    setIsUploading: (state, action: PayloadAction<boolean>) => {
      state.isUploading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    clearUploadProgress: (state, action: PayloadAction<string>) => {
      delete state.uploadProgress[action.payload];
    },
  },
});

export const {
  setDocuments,
  addDocument,
  removeDocument,
  updateDocumentType,
  updateDocumentStatus,
  setUploadProgress,
  setIsUploading,
  setError,
  clearUploadProgress,
} = documentsSlice.actions;

export default documentsSlice.reducer;
